import {
  type AdminAccCareTicketsCreateData,
  type AdminCatMediaCreateData,
  type AdminCatMediaUpdateData,
  type AdminCatWinesArchiveData,
  type AdminCatWinesDeleteData,
  type AdminCatWinesHistoryResponse,
  type AdminCatWinesSubmitResponse,
  type AdminCatWinesUsageResponse,
  type AdminExpBookingsApproveData,
  type AdminExpBookingsCancelData,
  type AdminExpBookingsDeclineData,
  type AdminExpBookingsGetResponse,
  type AdminExpReferenceGetResponse,
  type AdminExpTastingsArchiveData,
  type AdminExpTastingsCancelData,
  type AdminExpTastingsCancelResponse,
  type AdminExpTastingsContentUpdateData,
  type AdminExpTastingsGetResponse,
  type AdminGeoAppMarketsGetResponse,
  type AdminGeoAppMarketsListResponse,
  type AdminGeoRegionMediaCreateData,
  type AdminGeoRegionMediaUpdateData,
  type AdminGeoRegionsArchiveData,
  type AdminGeoRegionsHistoryResponse,
  type IntegrationCard,
  type UserConsentsOut,
  type WineAdmin,
  type WineLinksOut,
  zAdminCatWinesArchiveBody,
  zAdminCatWinesHistoryResponse,
  zAdminCatWinesSubmitResponse,
  zAdminExpBookingsCancelBody,
  zAdminExpBookingsDeclineBody,
  zAdminGeoAppMarketsGetResponse,
  zAdminGeoAppMarketsListResponse,
  zAdminGeoRegionsArchiveBody,
  zAdminGeoRegionsHistoryResponse,
} from './generated';

/**
 * Стабильные имена схем, которые генератор переименовывает при совпадении имён в разных модулях
 * бэкенда (появилось API приложения ACC: `ConsentOut` и `TicketCreateIn` стали
 * `AppModulesAccSchemas…`; модуль CAT: `ArchiveIn`, `ArchiveReason`, `HistoryPageOut`, `MediaIn`,
 * `MediaPatchIn` GEO стали `AppModulesGeo…`). Тип берётся от операции или родительской схемы —
 * появление одноимённой схемы в другом модуле его не ломает.
 */
export type TicketCreateIn = AdminAccCareTicketsCreateData['body'];
export type ConsentOut = NonNullable<UserConsentsOut['personal_data']>;
/** Строка «Истории изменений» интеграции (`HistoryItemOut` модуля интеграций — имя с коллизией). */
export type IntegrationHistoryItemOut = IntegrationCard['history'][number];

/** GEO: тело «Архивировать» (регион и винодельня — одна схема). */
export type ArchiveIn = AdminGeoRegionsArchiveData['body'];
/** GEO: причина архивирования — тип и значения (`as const` объект схемы тела операции). */
export type ArchiveReason = ArchiveIn['reason'];
export const ArchiveReason = zAdminGeoRegionsArchiveBody.shape.reason.enum;
/** GEO: страница «Истории изменений» (страна, регион, винодельня — одна схема). */
export type HistoryPageOut = AdminGeoRegionsHistoryResponse;
export const zHistoryPageOut = zAdminGeoRegionsHistoryResponse;
/** GEO: медиа региона и винодельни — добавление и правка подписи/порядка. */
export type MediaIn = AdminGeoRegionMediaCreateData['body'];
export type MediaPatchIn = AdminGeoRegionMediaUpdateData['body'];

/**
 * CAT (вина, сеты, дропы — одни схемы у трёх разделов; имена `ArchiveIn`, `StatusOut`,
 * `HistoryPageOut`, `MediaIn`, `LinkItemOut`… совпадают с GEO и SRV, генератор даёт им префиксы
 * `AppModulesCatSchemasAdmin…`). Тип — от операции вин: у сетов и дропов операции те же по форме.
 */
export type CatArchiveIn = AdminCatWinesArchiveData['body'];
export type CatArchiveReason = CatArchiveIn['reason'];
export const CatArchiveReason = zAdminCatWinesArchiveBody.shape.reason.enum;
/** CAT: ответ действий жизненного цикла (`{status, status_label, display_status…}`). */
export type CatStatusOut = AdminCatWinesSubmitResponse;
export const zCatStatusOut = zAdminCatWinesSubmitResponse;
/** CAT: «История изменений» записи каталога. */
export type CatHistoryPageOut = AdminCatWinesHistoryResponse;
export const zCatHistoryPageOut = zAdminCatWinesHistoryResponse;
/** CAT: использования записи перед удалением (`GET …/usage`). */
export type CatUsageOut = AdminCatWinesUsageResponse;
/** CAT: подтверждение удаления (`{confirm_name}`). */
export type CatConfirmNameIn = AdminCatWinesDeleteData['body'];
/** CAT: медиа вина, сета, дропа — добавление и замена/подпись/«Сделать обложкой». */
export type CatMediaIn = AdminCatMediaCreateData['body'];
export type CatMediaPatchIn = AdminCatMediaUpdateData['body'];
/** CAT: строка панелей «Где продаётся» и «Обучение и контент» карточки вина. */
export type CatLinkItemOut = WineLinksOut['sales_places'][number];
/** CAT: «Отзывы пользователей» карточки вина (только admin). */
export type CatReviewsOut = NonNullable<WineLinksOut['reviews']>;
/** CAT: классификация происхождения вина. */
export type CatOriginRefOut = NonNullable<WineAdmin['origin_classification']>;

/**
 * GEO «Языки, валюты и возраст»: страна приложения (`AppMarketOut` — имя совпадает со схемой
 * конфигурации приложения `/app/market-config`).
 */
export type AppMarketOut = AdminGeoAppMarketsGetResponse;
export const zAppMarketOut = zAdminGeoAppMarketsGetResponse;
export type AppMarketPageOut = AdminGeoAppMarketsListResponse;
export const zAppMarketPageOut = zAdminGeoAppMarketsListResponse;

/**
 * EXP (дегустации, экскурсии, поездки, брони): у схем событий общие имена (`ContentIn`, `PairIn`,
 * `StepIn`, `HistoryOut`, `LineOut`, `OptionOut`, `RefOut`, `CardCan`…) — их тип берётся от операций
 * (у трёх видов событий схемы одни и те же), чтобы одноимённая схема другого модуля их не ломала.
 */
export type ExpReferenceOut = AdminExpReferenceGetResponse;
export type ExpOptionOut = ExpReferenceOut['formats'][number];
export type ExpRefOut = ExpReferenceOut['regions'][number];
export type ExpHostOut = ExpReferenceOut['hosts'][number];
export type ExpTimezoneOut = ExpReferenceOut['timezones'][number];
export type ExpContentIn = AdminExpTastingsContentUpdateData['body'];
export type ExpPairIn = NonNullable<ExpContentIn['details']>[number];
export type ExpStepIn = NonNullable<ExpContentIn['steps']>[number];
export type ExpMediaIn = NonNullable<ExpContentIn['media']>[number];
export type ExpPairOut = AdminExpTastingsGetResponse['details'][number];
export type ExpStepOut = AdminExpTastingsGetResponse['steps'][number];
export type ExpMediaOut = AdminExpTastingsGetResponse['media'][number];
export type ExpTileOut = AdminExpTastingsGetResponse['summary'][number];
export type ExpCardCan = AdminExpTastingsGetResponse['can'];
export type ExpModerationOut = AdminExpTastingsGetResponse['moderation'];
export type ExpArchiveIn = AdminExpTastingsArchiveData['body'];
export type ExpArchiveReason = ExpArchiveIn['reason'];
export type ExpCancelEventIn = AdminExpTastingsCancelData['body'];
export type ExpCancelEventOut = AdminExpTastingsCancelResponse;
/** Брони: строки истории и расчёта карточки, тела действий и причины отказа и отмены. */
export type ExpHistoryOut = AdminExpBookingsGetResponse['history'][number];
export type ExpLineOut = AdminExpBookingsGetResponse['calc_lines'][number];
export type BookingApproveIn = AdminExpBookingsApproveData['body'];
export type BookingDeclineIn = AdminExpBookingsDeclineData['body'];
export type BookingCancelIn = AdminExpBookingsCancelData['body'];
export type BookingDeclineReason = BookingDeclineIn['reason'];
export const BookingDeclineReason = zAdminExpBookingsDeclineBody.shape.reason.enum;
export type BookingCancelReason = BookingCancelIn['reason'];
export const BookingCancelReason = zAdminExpBookingsCancelBody.shape.reason.enum;
